import React, { useState, useRef, memo } from 'react';
import { motion } from 'framer-motion';
import { Upload, UserRound, X, AlertTriangle } from 'lucide-react';
import { Logo } from './Logo';
import { useApp, CurrentUser } from './store';
import { downscaleImage } from './image';
import { isGraphConfigured } from './graph/config';
// Neutral fallback used only if the person doesn't upload a photo.
const DEFAULT_AVATAR = 'https://i.pravatar.cc/160?img=68';
// Blocks the app on first visit until the person tells us who they are. Their
// choice is stored per browser (no password), so they are never asked again on
// this machine — posts, comments, kudos, and the top-bar avatar all reflect them.
export function IdentityGate() {
  // In Microsoft mode, identity is resolved silently via MSAL/Graph, so the
  // "who's here?" welcome prompt is never shown. Guarding here (before any
  // hooks run) keeps the hook order stable in local mode.
  if (isGraphConfigured()) return null;
  return <IdentityGateForm data-id="element-136" data-mp="3un3m635br8zw" />;
}
function IdentityGateForm() {
  const {
    persistUser,
    setIdentity,
    startBrowsing
  } = useApp();
  const fileRef = useRef<HTMLInputElement>(null);
  const [form, setForm] = useState({
    name: '',
    title: '',
    location: '',
    avatar: ''
  });
  const [error, setError] = useState('');
  const [processing, setProcessing] = useState(false);
  const [saveWarning, setSaveWarning] = useState(false);
  const onFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setError('Please choose an image file.');
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      setError('Please choose an image under 8 MB.');
      return;
    }
    setError('');
    setProcessing(true);
    try {
      // Downscale + compress so the stored profile stays tiny.
      const small = await downscaleImage(file, 256, 0.8);
      setForm(f => ({
        ...f,
        avatar: small
      }));
    } catch {
      setError('Sorry, we couldn’t process that image. Try a different one.');
    } finally {
      setProcessing(false);
    }
  };
  const buildIdentity = (): CurrentUser => {
    const slug = form.name.trim().toLowerCase().replace(/\s+/g, '.');
    return {
      name: form.name.trim(),
      title: form.title.trim() || 'Team Member',
      location: form.location.trim() || 'Independence Home Loans',
      email: `${slug}@indephl.com`,
      avatar: form.avatar || DEFAULT_AVATAR
    };
  };
  const save = (ev: React.FormEvent) => {
    ev.preventDefault();
    if (!form.name.trim()) return;
    const identity = buildIdentity();
    // Self-check: try to persist first. If it fails, warn instead of silently
    // continuing (the person would otherwise be asked again next time).
    const ok = persistUser(identity);
    if (ok) {
      setIdentity(identity);
    } else {
      setSaveWarning(true);
    }
  };
  const continueAnyway = () => {
    // Commit in memory for this session even though it couldn't be saved.
    setIdentity(buildIdentity());
  };
  return <div className="fixed inset-0 z-[100] bg-navy-900/80 backdrop-blur-sm flex items-center justify-center p-4" data-id="element-137" data-mp="f54e2dbuum67">
      <motion.div initial={{
      opacity: 0,
      y: 16,
      scale: 0.98
    }} animate={{
      opacity: 1,
      y: 0,
      scale: 1
    }} transition={{
      duration: 0.25
    }} className="w-full max-w-lg bg-white rounded-2xl overflow-hidden shadow-2xl" data-id="element-138" data-mp="swsomc6gh2l2">
        {/* Header */}
        <div className="bg-navy-600 px-6 py-5 text-center" data-id="element-139" data-mp="a5rsrx0mtn5q">
          <div className="flex justify-center mb-3" data-id="element-140" data-mp="a7cohpyonc56">
            <Logo className="h-10" data-id="element-141" data-mp="56yh8a4wo687" />
          </div>
          <h1 className="text-xl font-serif font-bold text-white" data-id="element-142" data-mp="55dis9fh0352">
            Welcome — who's here?
          </h1>
          <p className="text-navy-100 text-sm mt-1" data-id="element-143" data-mp="1ln18c8gn3v1r">
            Tell us who you are so your posts and kudos show up as you. We'll
            remember you on this computer.
          </p>
        </div>

        <form onSubmit={save} className="p-5 space-y-4" data-id="element-144" data-mp="2byeguw670k9a">
          {/* Photo upload */}
          <div className="flex items-center gap-4" data-id="element-145" data-mp="2gx7jftjxyquo">
            <div className="relative shrink-0" data-id="element-146" data-mp="2lty998ljt0ta">
              {form.avatar ? <img src={form.avatar} alt="Your photo" className="w-16 h-16 rounded-full object-cover ring-2 ring-gold-400" data-id="element-147" data-mp="2qt127uo97gkw" /> : <div className="w-16 h-16 rounded-full bg-navy-50 text-navy-300 flex items-center justify-center ring-1 ring-navy-100" data-id="element-148" data-mp="2zt7e5c4b08hy">
                  <UserRound className="w-7 h-7" data-id="element-149" data-mp="34lhbhd7r0au9" />
                </div>}
              {form.avatar && <button type="button" onClick={() => setForm(f => ({
              ...f,
              avatar: ''
            }))} className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-navy-600 text-white flex items-center justify-center hover:bg-navy-700" aria-label="Remove photo" data-id="element-150" data-mp="39lmw49flox2u">
                  <X className="w-3 h-3" data-id="element-151" data-mp="xxi2sua1bhtu" />
                </button>}
            </div>
            <div data-id="element-152" data-mp="sydvk96o7u65">
              <button type="button" onClick={() => fileRef.current?.click()} disabled={processing} className="inline-flex items-center gap-2 px-3.5 py-2 text-sm font-semibold text-navy-700 bg-navy-50 rounded-lg hover:bg-navy-100 transition-colors disabled:opacity-50" data-id="element-153" data-mp="syx0mpb82o2r">
                <Upload className="w-4 h-4" data-id="element-154" data-mp="1bwhpvxhwfonw" />
                {processing ? 'Processing…' : form.avatar ? 'Change photo' : 'Upload photo'}
              </button>
              <p className="text-xs text-navy-400 mt-1.5" data-id="element-155" data-mp="24k3t34n7sra8">
                Optional · we’ll resize it automatically.
              </p>
              <input ref={fileRef} type="file" accept="image/*" onChange={onFile} className="hidden" data-id="element-156" data-mp="2pg3bl7l85i3" />
            </div>
          </div>
          {error && <p className="text-sm text-red-600" data-id="element-157" data-mp="3u1hfqd91kfhs">{error}</p>}

          <label className="block" data-id="element-158" data-mp="3tzwmcqygr2li">
            <span className="block text-xs font-semibold uppercase tracking-wide text-navy-500 mb-1.5" data-id="element-159" data-mp="3f48mfe06h1nf">
              Your name
            </span>
            <input autoFocus value={form.name} onChange={e => setForm(f => ({
            ...f,
            name: e.target.value
          }))} placeholder="Jane Doe" className="w-full px-3 py-2.5 rounded-lg bg-navy-50 border border-transparent focus:border-gold-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-gold-400/30 text-sm text-navy-900" data-id="element-160" data-mp="3k54pxqgmalsb" />
          </label>
          <div className="grid sm:grid-cols-2 gap-3" data-id="element-161" data-mp="1jdm96tyxfvao">
            <label className="block" data-id="element-162" data-mp="1ehoust784z0j">
              <span className="block text-xs font-semibold uppercase tracking-wide text-navy-500 mb-1.5" data-id="element-163" data-mp="1eej81km2i97z">
                Title
              </span>
              <input value={form.title} onChange={e => setForm(f => ({
              ...f,
              title: e.target.value
            }))} placeholder="Loan Officer" className="w-full px-3 py-2.5 rounded-lg bg-navy-50 border border-transparent focus:border-gold-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-gold-400/30 text-sm text-navy-900" data-id="element-164" data-mp="1nh25fp733tmu" />
            </label>
            <label className="block" data-id="element-165" data-mp="gtn6u9farmm7">
              <span className="block text-xs font-semibold uppercase tracking-wide text-navy-500 mb-1.5" data-id="element-166" data-mp="guos16q11phn">
                Location
              </span>
              <input value={form.location} onChange={e => setForm(f => ({
              ...f,
              location: e.target.value
            }))} placeholder="Dallas, TX" className="w-full px-3 py-2.5 rounded-lg bg-navy-50 border border-transparent focus:border-gold-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-gold-400/30 text-sm text-navy-900" data-id="element-167" data-mp="11bfa9gaisnkx" />
            </label>
          </div>

          {/* Persistence warning (only shown if the save self-check failed) */}
          {saveWarning && <div className="flex items-start gap-2.5 p-3 rounded-lg bg-gold-50 border border-gold-200" data-id="element-168" data-mp="wd3z928ho714">
              <AlertTriangle className="w-4 h-4 text-gold-700 mt-0.5 shrink-0" data-id="element-169" data-mp="22t7lpeqxcpn3" />
              <p className="text-sm text-navy-700" data-id="element-170" data-mp="22vk39qsrsxzm">
                We couldn’t save your profile on this device (browser storage
                may be full or blocked). You can continue for now, but you may
                be asked again next time.
              </p>
            </div>}

          <div className="flex items-center justify-between pt-1" data-id="element-171" data-mp="2czgz8xx1hgmr">
            {/* Read-only escape hatch. Lets a first-time visitor look around
             without an identity — posting/commenting/kudos stay gated until
             they introduce themselves. */}
            <button type="button" onClick={startBrowsing} className="text-sm font-medium text-navy-500 hover:text-navy-800 underline underline-offset-2 transition-colors" data-id="element-172" data-mp="1z6j1x611degk">
              Just browsing for now
            </button>
            {saveWarning ? <button type="button" onClick={continueAnyway} className="px-5 py-2.5 text-sm font-bold text-navy-900 bg-gold-500 rounded-lg hover:bg-gold-400 transition-colors" data-id="element-173" data-mp="1l7rm9f6izkqb">
                Continue anyway
              </button> : <button type="submit" disabled={!form.name.trim() || processing} className="px-5 py-2.5 text-sm font-bold text-navy-900 bg-gold-500 rounded-lg hover:bg-gold-400 transition-colors disabled:opacity-40 disabled:cursor-not-allowed" data-id="element-174" data-mp="3aizht6oaa9z3">
                Continue
              </button>}
          </div>
          <p className="text-center text-xs text-navy-400" data-id="element-175" data-mp="2rqompyzvfgbj">
            You can add your details anytime — just try to post.
          </p>
        </form>
      </motion.div>
    </div>;
}